'use client';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { formatDate } from '@/components/usage/helpers';
import { useCountdown } from '@/components/usage/useCountdown';
import { useAuthUser } from '@/hooks/useAuthUser';
import { Link } from '@/i18n/routing';
import { useAppDispatch, useAppSelector } from '@/lib/redux/hooks';
import {
  selectDocGenLimit,
  selectDocGenUsed,
  selectErrorMessage,
  selectErrorThinkingMode,
  selectQuotaErrorCode,
  selectShouldShowOverflowPrompt,
} from '@/lib/redux/selectors/errorModalSelectors';
import { closeErrorModal } from '@/lib/redux/slices/errorModalSlice';
import { setThinkingMode } from '@/lib/redux/slices/uiSettingsSlice';
import {
  selectIsPaidUser,
  selectUsageData,
} from '@/lib/redux/slices/usageSlice';
import type { ThinkingMode } from '@/types/thinkingTypes';
import {
  AlertTriangle,
  BarChart2,
  Clock,
  CreditCard,
  Lock,
  TrendingUp,
  Zap,
} from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useLocale } from 'next-intl';
import { usePostHog } from 'posthog-js/react';
import type React from 'react';
import { useCallback, useEffect, useRef } from 'react';
import {
  type ActionDescriptor,
  type FactLine,
  type OverflowIcon,
  getScenarioDescriptor,
} from './overflowScenario';

/** Maps each icon token to its concrete lucide icon + status colour. */
const ICON_BY_TOKEN: Record<OverflowIcon, React.ReactNode> = {
  AlertTriangle: <AlertTriangle className='h-8 w-8 text-status-warning' />,
  Lock: <Lock className='h-8 w-8 text-status-error' />,
  LockInfo: <Lock className='h-8 w-8 text-status-info' />,
  Clock: <Clock className='h-8 w-8 text-status-warning' />,
  CreditCard: <CreditCard className='h-8 w-8 text-status-warning' />,
  Zap: <Zap className='h-8 w-8 text-status-warning' />,
  TrendingUp: <TrendingUp className='h-8 w-8 text-status-error' />,
};

/**
 * Renders a fact-line descriptor into JSX, resolving any i18n keys via `t`.
 * The MODE_NOT_AVAILABLE icon's `text-status-info` colour exception is applied
 * via the dedicated `LockInfo` token in `ICON_BY_TOKEN` above.
 */
function renderFactLine(
  factLine: FactLine | null,
  t: ReturnType<typeof useTranslations>
): React.ReactNode {
  if (!factLine) {
    return null;
  }

  if (factLine.kind === 'raw') {
    return factLine.italicMuted ? (
      <span className='text-content-muted italic'>{factLine.text}</span>
    ) : (
      <span>{factLine.text}</span>
    );
  }

  // Resolve any param values that are themselves i18n keys (e.g. mode name).
  const resolvedParamKeys = factLine.paramKeys
    ? Object.fromEntries(
        Object.entries(factLine.paramKeys).map(([param, key]) => [
          param,
          t(key as Parameters<typeof t>[0]),
        ])
      )
    : {};
  const params = { ...factLine.params, ...resolvedParamKeys };

  return <span>{t(factLine.key as Parameters<typeof t>[0], params)}</span>;
}

function renderActionButton(
  action: ActionDescriptor,
  variant: 'brandGradient' | 'brandOutline',
  t: ReturnType<typeof useTranslations>,
  onClick: (switchToMode?: ThinkingMode) => void
) {
  const label = action.labelTextKey
    ? t(action.labelTextKey as Parameters<typeof t>[0])
    : t(action.labelKey as Parameters<typeof t>[0]); // dynamic key

  if (action.href) {
    return (
      <Button
        variant={variant}
        size='lg'
        className='w-full min-h-[44px]'
        asChild
      >
        <Link href={action.href as '/pricing'} onClick={() => onClick()}>
          {label}
        </Link>
      </Button>
    );
  }

  return (
    <Button
      variant={variant}
      size='lg'
      className='w-full min-h-[44px]'
      onClick={() => onClick(action.switchToMode)}
    >
      {label}
    </Button>
  );
}

/**
 * Context-aware overflow/upgrade prompt dialog.
 *
 * Shows different content based on the structured error code from the backend
 * (DAILY_LIMIT_REACHED, MONTHLY_CAP_REACHED, TRIAL_EXPIRED, etc.).
 *
 * Every scenario now answers: (1) WHAT is blocked, (2) WHY, (3) what's still
 * available / how much is left + when it resets, (4) the way out, with a link
 * to the usage page so the modal is never a dead end.
 *
 * @see GitHub issues #2451, #5482
 */
const OverflowPrompt: React.FC = () => {
  const dispatch = useAppDispatch();
  const t = useTranslations('OverflowPrompt');
  const locale = useLocale();
  const posthog = usePostHog();
  // #11315: only the org OWNER can act on ORG_SUSPENDED by resubscribing —
  // an ordinary member is pointed at their personal account instead.
  const { userPlan, isBackendUserResolved, isOrgOwner } = useAuthUser();

  const isOpen = useAppSelector(selectShouldShowOverflowPrompt);
  const quotaErrorCode = useAppSelector(selectQuotaErrorCode);
  const thinkingMode = useAppSelector(selectErrorThinkingMode);
  const isPaidUser = useAppSelector(selectIsPaidUser);
  const usageData = useAppSelector(selectUsageData);
  const backendMessage = useAppSelector(selectErrorMessage);
  const docGenUsed = useAppSelector(selectDocGenUsed);
  const docGenLimit = useAppSelector(selectDocGenLimit);

  // Reverse-trial conversion instrumentation (#9876): this dialog IS the
  // upgrade CTA fired at the moment a user hits their quota wall — the
  // conversion lever quota_types.py documents but that had no PostHog event
  // behind it. Fire on the actual render (isOpen && quotaErrorCode, the same
  // condition the early-return below gates on), not on the backend response
  // that triggers it, so "shown" means "rendered to the user" per the issue.
  // This component is a single app-wide singleton (mounted once in the root
  // locale layout) whose `isOpen` toggles across the whole session lifetime,
  // unlike a per-mount impression — so a ref alone can't gate "once ever";
  // it must gate "once per open", reset on close so the next quota wall
  // fires again.
  //
  // Gated on `isBackendUserResolved` (peer-review finding): `userPlan`
  // resolves to a literal 'trial' fallback before the backend /users/me
  // fetch completes (auth-context.tsx), not undefined — so without this
  // gate a dialog that opens in that window would permanently mistag a
  // paid user's event as plan_tag: 'trial' (the ref marks "shown" even
  // though it fired with the wrong value). Deferring until resolved
  // matches the existing convention at blog-studio/layout.tsx and
  // PracticeGate.tsx for the same field.
  const shownLoggedRef = useRef(false);
  useEffect(() => {
    if (isOpen && quotaErrorCode) {
      if (!shownLoggedRef.current && isBackendUserResolved) {
        shownLoggedRef.current = true;
        posthog?.capture('quota_denial_shown', {
          mode: thinkingMode,
          plan_tag: userPlan,
          reason: quotaErrorCode,
        });
      }
    } else {
      shownLoggedRef.current = false;
    }
  }, [
    isOpen,
    quotaErrorCode,
    thinkingMode,
    userPlan,
    posthog,
    isBackendUserResolved,
  ]);

  const zadanie = usageData?.zadanie ?? null;
  const fullRemaining = zadanie?.daily.full_remaining ?? null;
  const dailyResetsAt = zadanie?.daily.resets_at ?? null;
  const monthlyTotal = zadanie?.monthly.total ?? null;
  const monthlyCap = zadanie?.monthly.soft_cap ?? null;
  const monthlyPeriodEnd = zadanie?.monthly.period_end ?? null;

  // Live countdown to the next daily reset (updates every 60s).
  const countdown = useCountdown(dailyResetsAt);

  const handleAction = useCallback(
    (switchToMode?: ThinkingMode) => {
      if (switchToMode) {
        dispatch(setThinkingMode(switchToMode));
      }
      dispatch(closeErrorModal());
    },
    [dispatch]
  );

  if (!isOpen || !quotaErrorCode) {
    return null;
  }

  const scenario = getScenarioDescriptor({
    errorCode: quotaErrorCode,
    thinkingMode,
    isPaidUser,
    fullRemaining,
    monthlyTotal,
    monthlyCap,
    monthlyPeriodEndFormatted: formatDate(monthlyPeriodEnd, locale),
    countdown,
    backendMessage,
    docGenUsed,
    docGenLimit,
    isOrgOwner,
  });

  const factLineContent = renderFactLine(scenario.factLine, t);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleAction()}>
      <DialogContent className='max-w-md bg-surface-base border border-line-default'>
        <DialogHeader className='items-center space-y-3 pt-2'>
          {ICON_BY_TOKEN[scenario.icon]}
          <DialogTitle className='text-center text-lg text-content-primary'>
            {t(scenario.titleKey as Parameters<typeof t>[0])}
          </DialogTitle>
          <DialogDescription className='text-center text-sm text-content-secondary'>
            {t(scenario.descriptionKey as Parameters<typeof t>[0])}
          </DialogDescription>
          {/* Data-grounded fact line: numbers, reset time, available modes */}
          {factLineContent && (
            <p className='text-center text-xs text-content-muted mt-1'>
              {factLineContent}
            </p>
          )}
        </DialogHeader>

        <div className='flex flex-col gap-3 mt-4'>
          {/* Primary CTA */}
          {renderActionButton(
            scenario.primaryAction,
            'brandGradient',
            t,
            handleAction
          )}

          {/* Secondary CTA */}
          {scenario.secondaryAction &&
            renderActionButton(
              scenario.secondaryAction,
              'brandOutline',
              t,
              handleAction
            )}

          {/* Tertiary: usage page link — always present so modal is never a dead end */}
          <Link
            href='/user-profile/usage'
            onClick={() => handleAction()}
            className='text-center text-xs text-brand-tertiary/70 hover:text-brand-tertiary transition-colors py-1 min-h-[44px] flex items-center justify-center gap-1'
            aria-label={t('viewUsageAriaLabel')}
          >
            <BarChart2 className='h-3 w-3' aria-hidden='true' />
            {t('viewUsage')}
          </Link>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OverflowPrompt;
